import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import { getCurrentLocation, buildLocationParams } from '../utils/location';
import { formatIndianDate } from '../utils/formatters';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Wrench, AlertTriangle, Clock, MessageSquare, Eye, Edit, CheckCircle, ListTodo, AlertCircle, Calendar, MapPin, Phone, Building2 } from 'lucide-react';

const EngineerDashboard = () => {
  const { user, logout } = useAuth();
  const [services, setServices] = useState([]);
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    missed_due_date: 0,
    completed: 0,
    cancelled: 0
  });
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [selectedService, setSelectedService] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [updateStatusDialogOpen, setUpdateStatusDialogOpen] = useState(false);
  const [closureDialogOpen, setClosureDialogOpen] = useState(false);
  const [commentDialogOpen, setCommentDialogOpen] = useState(false);

  const [closureData, setClosureData] = useState({
    payment_status: '',
    bill_type: '',
    payment_mode: '',
    payment_notes: '',
    service_charge: ''
  });

  const [commentData, setCommentData] = useState({
    comment: '',
    next_visit_date: ''
  });

  // Check session validity on dashboard load
  useEffect(() => {
    const checkSession = async () => {
      try {
        const response = await api.get('/auth/check-session');
        if (!response.data.valid) {
          toast.error(response.data.reason || 'Session expired. Please login again.');
          logout();
        }
      } catch (error) {
        if (error.response?.status === 401) {
          logout();
        }
      }
    };
    
    if (user) {
      checkSession();
    }
  }, [user, logout]);

  useEffect(() => {
    fetchServices();
    fetchStats();
  }, []);

  const fetchServices = async () => {
    setLoading(true);
    try {
      const response = await api.get('/services/');
      const sortedServices = response.data.sort((a, b) => new Date(a.due_date) - new Date(b.due_date));
      setServices(sortedServices);
    } catch (error) {
      toast.error('Failed to fetch services');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await api.get('/services/engineer-stats');
      setStats(response.data);
    } catch (error) {
      console.error(error);
    }
  };

  const handleViewService = async (service) => {
    try {
      const response = await api.get(`/services/${service.id}`);
      setSelectedService(response.data);
      setViewDialogOpen(true);
    } catch (error) {
      toast.error('Failed to fetch service details');
    }
  };

  const handleOpenStatusUpdate = (service) => {
    setSelectedService(service);
    setUpdateStatusDialogOpen(true);
  };

  const handleStatusUpdate = async (newStatus) => {
    if (newStatus === 'Closed') {
      setUpdateStatusDialogOpen(false);
      setClosureData({ payment_status: '', bill_type: '', payment_mode: '', payment_notes: '', service_charge: '' });
      setClosureDialogOpen(true);
    } else {
      try {
        let location;
        try {
          location = await getCurrentLocation();
          toast.info('Location captured', { duration: 1500 });
        } catch (locError) {
          toast.error(locError.message);
          return;
        }
        const locationParams = buildLocationParams(location);
        await api.put(`/services/${selectedService.id}?${locationParams}`, { status: newStatus });
        toast.success(`Status updated to ${newStatus}`);
        setUpdateStatusDialogOpen(false);
        fetchServices();
        fetchStats();
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to update');
      }
    }
  };

  const handleClosureSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      let location;
      try {
        location = await getCurrentLocation();
        toast.info('Location captured', { duration: 1500 });
      } catch (locError) {
        toast.error(locError.message);
        setLoading(false);
        return;
      }
      const updateData = {
        status: 'Closed',
        payment_status: closureData.payment_status,
        payment_notes: closureData.payment_notes,
        service_charge: closureData.service_charge ? parseFloat(closureData.service_charge) : null
      };
      if (closureData.payment_status === 'Paid') {
        updateData.bill_type = closureData.bill_type;
        updateData.payment_mode = closureData.bill_type === 'Off Bill' ? 'Cash' : closureData.payment_mode;
      }
      const locationParams = buildLocationParams(location);
      await api.put(`/services/${selectedService.id}?${locationParams}`, updateData);
      toast.success('Service closed successfully');
      setClosureDialogOpen(false);
      fetchServices();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to close');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenCommentDialog = (service) => {
    setSelectedService(service);
    setCommentData({ comment: '', next_visit_date: '' });
    setCommentDialogOpen(true);
  };

  const handleAddComment = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      let location;
      try {
        location = await getCurrentLocation();
        toast.info('Location captured', { duration: 1500 });
      } catch (locError) {
        toast.error(locError.message);
        setLoading(false);
        return;
      }
      const locationParams = buildLocationParams(location);
      await api.post(`/services/${selectedService.id}/comments?${locationParams}`, commentData);
      toast.success('Comment added');
      setCommentDialogOpen(false);
      fetchServices();
    } catch (error) {
      toast.error('Failed to add comment');
    } finally {
      setLoading(false);
    }
  };

  const isOverdue = (dueDate, status) => {
    if (status === 'Closed' || status === 'Canceled') return false;
    return new Date(dueDate) < new Date();
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'Emergency': return 'bg-red-600 text-white';
      case 'Urgent': return 'bg-orange-500 text-white';
      default: return 'bg-green-100 text-green-800';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'New': return 'bg-blue-100 text-blue-800';
      case 'On Process': return 'bg-yellow-100 text-yellow-800';
      case 'Closed': return 'bg-green-100 text-green-800';
      case 'Canceled': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const filteredServices = services.filter(service => {
    if (statusFilter === 'pending') return service.status === 'New' || service.status === 'On Process';
    if (statusFilter === 'overdue') return isOverdue(service.due_date, service.status);
    if (statusFilter === 'all') return true;
    return service.status === statusFilter;
  });

  const todayServices = services.filter(service => {
    const today = new Date().toDateString();
    const dueDate = new Date(service.due_date).toDateString();
    return dueDate === today && (service.status === 'New' || service.status === 'On Process');
  });

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header - Mobile Optimized */}
      <div className="px-1">
        <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900">My Services</h1>
        <p className="text-sm sm:text-base text-gray-600 mt-1">Welcome, {user?.name}!</p>
      </div>

      {/* Stats Cards - Mobile Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2 sm:gap-4">
        <Card className="cursor-pointer active:scale-95 transition-transform" onClick={() => setStatusFilter('all')}>
          <CardContent className="p-3 sm:p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs sm:text-sm text-gray-600">Total</p>
                <p className="text-xl sm:text-2xl font-bold text-blue-600">{stats.total}</p>
              </div>
              <ListTodo className="text-blue-600 h-5 w-5 sm:h-6 sm:w-6" />
            </div>
          </CardContent>
        </Card>

        <Card className="cursor-pointer active:scale-95 transition-transform" onClick={() => setStatusFilter('pending')}>
          <CardContent className="p-3 sm:p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs sm:text-sm text-gray-600">Pending</p>
                <p className="text-xl sm:text-2xl font-bold text-orange-600">{stats.pending}</p>
              </div>
              <Clock className="text-orange-600 h-5 w-5 sm:h-6 sm:w-6" />
            </div>
          </CardContent>
        </Card>

        <Card className={`cursor-pointer active:scale-95 transition-transform ${stats.missed_due_date > 0 ? 'border-red-300 bg-red-50' : ''}`} onClick={() => setStatusFilter('overdue')}>
          <CardContent className="p-3 sm:p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs sm:text-sm text-gray-600">Overdue</p>
                <p className={`text-xl sm:text-2xl font-bold ${stats.missed_due_date > 0 ? 'text-red-600' : 'text-gray-400'}`}>{stats.missed_due_date}</p>
              </div>
              <AlertTriangle className={`h-5 w-5 sm:h-6 sm:w-6 ${stats.missed_due_date > 0 ? 'text-red-600 animate-pulse' : 'text-gray-400'}`} />
            </div>
          </CardContent>
        </Card>

        <Card className="cursor-pointer active:scale-95 transition-transform" onClick={() => setStatusFilter('Closed')}>
          <CardContent className="p-3 sm:p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs sm:text-sm text-gray-600">Done</p>
                <p className="text-xl sm:text-2xl font-bold text-green-600">{stats.completed}</p>
              </div>
              <CheckCircle className="text-green-600 h-5 w-5 sm:h-6 sm:w-6" />
            </div>
          </CardContent>
        </Card>

        <Card className="cursor-pointer active:scale-95 transition-transform col-span-2 sm:col-span-1" onClick={() => setStatusFilter('Canceled')}>
          <CardContent className="p-3 sm:p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs sm:text-sm text-gray-600">Cancelled</p>
                <p className="text-xl sm:text-2xl font-bold text-red-500">{stats.cancelled}</p>
              </div>
              <AlertCircle className="text-red-500 h-5 w-5 sm:h-6 sm:w-6" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Today's Schedule - Mobile Card View */}
      {todayServices.length > 0 && (
        <Card className="border-blue-200 bg-blue-50">
          <CardHeader className="p-3 sm:p-4 pb-2">
            <CardTitle className="flex items-center gap-2 text-blue-800 text-base sm:text-lg">
              <Calendar size={18} />
              Today ({todayServices.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-3 sm:p-4 pt-0">
            <div className="space-y-2">
              {todayServices.map(service => (
                <div key={service.id} className="flex flex-col sm:flex-row sm:items-center justify-between p-3 bg-white rounded-lg border gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-sm">{service.service_no}</span>
                      <Badge className={getPriorityColor(service.priority)} variant="secondary">
                        {service.priority}
                      </Badge>
                    </div>
                    <p className="text-sm text-gray-600 truncate">{service.customer?.company_name}</p>
                    {service.due_time && <Badge variant="outline" className="text-xs mt-1">{service.due_time}</Badge>}
                  </div>
                  <Button size="sm" onClick={() => handleViewService(service)} className="w-full sm:w-auto">
                    View
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Services List - Mobile Card View */}
      <Card>
        <CardHeader className="p-3 sm:p-4 pb-2">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
            <div>
              <CardTitle className="text-base sm:text-lg">My Services</CardTitle>
              <CardDescription className="text-xs sm:text-sm">Manage your tasks</CardDescription>
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-40">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="overdue">Overdue</SelectItem>
                <SelectItem value="New">New</SelectItem>
                <SelectItem value="On Process">On Process</SelectItem>
                <SelectItem value="Closed">Closed</SelectItem>
                <SelectItem value="Canceled">Canceled</SelectItem>
                <SelectItem value="all">All</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent className="p-3 sm:p-4 pt-0">
          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            </div>
          ) : filteredServices.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Wrench size={40} className="mx-auto mb-4 text-gray-300" />
              <p className="text-sm">No services found</p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredServices.map((service) => (
                <div
                  key={service.id}
                  className={`p-3 sm:p-4 rounded-lg border ${isOverdue(service.due_date, service.status) ? 'bg-red-50 border-red-200' : 'bg-white'}`}
                >
                  {/* Service Header */}
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-sm sm:text-base">{service.service_no}</span>
                        <Badge className={getPriorityColor(service.priority)} variant="secondary">
                          {service.priority}
                        </Badge>
                        <Badge className={getStatusColor(service.status)}>
                          {service.status}
                        </Badge>
                      </div>
                    </div>
                  </div>

                  {/* Customer Info */}
                  <div className="mt-2 space-y-1">
                    <div className="flex items-center gap-2 text-sm">
                      <Building2 size={14} className="text-gray-400 flex-shrink-0" />
                      <span className="truncate">{service.customer?.company_name || 'N/A'}</span>
                    </div>
                    {service.customer?.mobile && (
                      <a href={`tel:${service.customer.mobile}`} className="flex items-center gap-2 text-sm text-blue-600">
                        <Phone size={14} className="flex-shrink-0" />
                        {service.customer.mobile}
                      </a>
                    )}
                  </div>

                  {/* Service Type & Date */}
                  <div className="mt-2 flex flex-wrap items-center gap-2 text-xs sm:text-sm">
                    <Badge variant="outline">{service.service_type}</Badge>
                    <span className={`flex items-center gap-1 ${isOverdue(service.due_date, service.status) ? 'text-red-600 font-medium' : 'text-gray-500'}`}>
                      {isOverdue(service.due_date, service.status) && <AlertTriangle size={12} />}
                      Due: {new Date(service.due_date).toLocaleDateString('en-IN')}
                      {service.due_time && ` ${service.due_time}`}
                    </span>
                  </div>

                  {/* Actions */}
                  <div className="mt-3 flex flex-wrap gap-2">
                    <Button size="sm" variant="outline" onClick={() => handleViewService(service)} className="flex-1 sm:flex-none">
                      <Eye size={14} className="mr-1" /> View
                    </Button>
                    {service.status !== 'Closed' && service.status !== 'Canceled' && (
                      <>
                        <Button size="sm" variant="outline" onClick={() => handleOpenStatusUpdate(service)} className="flex-1 sm:flex-none">
                          <Edit size={14} className="mr-1" /> Update
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => handleOpenCommentDialog(service)} className="flex-1 sm:flex-none">
                          <MessageSquare size={14} className="mr-1" /> Note
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* View Service Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto mx-4">
          <DialogHeader>
            <DialogTitle className="text-lg">{selectedService?.service_no}</DialogTitle>
          </DialogHeader>
          {selectedService && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <Label className="text-gray-500 text-xs">Customer</Label>
                  <p className="font-medium">{selectedService.customer?.company_name}</p>
                  <a href={`tel:${selectedService.customer?.mobile}`} className="text-blue-600 text-sm">
                    {selectedService.customer?.mobile}
                  </a>
                </div>
                <div>
                  <Label className="text-gray-500 text-xs">Type</Label>
                  <p className="font-medium">{selectedService.service_type}</p>
                </div>
                <div>
                  <Label className="text-gray-500 text-xs">Priority</Label>
                  <Badge className={getPriorityColor(selectedService.priority)}>{selectedService.priority}</Badge>
                </div>
                <div>
                  <Label className="text-gray-500 text-xs">Status</Label>
                  <Badge className={getStatusColor(selectedService.status)}>{selectedService.status}</Badge>
                </div>
                <div className="col-span-2">
                  <Label className="text-gray-500 text-xs">Due Date</Label>
                  <p className="font-medium">
                    {new Date(selectedService.due_date).toLocaleDateString('en-IN')}
                    {selectedService.due_time && ` ${selectedService.due_time}`}
                  </p>
                </div>
              </div>
              
              <div>
                <Label className="text-gray-500 text-xs">Description</Label>
                <p className="mt-1 p-2 bg-gray-50 rounded text-sm">{selectedService.description}</p>
              </div>

              {selectedService.service_type === 'In-Office Service' && selectedService.device_reference_number && (
                <div className="p-3 bg-purple-50 rounded border border-purple-200">
                  <Label className="text-purple-700 text-xs">Device Reference</Label>
                  <p className="text-lg font-mono font-bold text-purple-800">{selectedService.device_reference_number}</p>
                </div>
              )}

              {selectedService.comments?.length > 0 && (
                <div>
                  <Label className="text-gray-500 text-xs flex items-center gap-1">
                    <MessageSquare size={12} /> Notes ({selectedService.comments.length})
                  </Label>
                  <div className="mt-2 space-y-2 max-h-40 overflow-y-auto">
                    {selectedService.comments.map(comment => (
                      <div key={comment.id} className="p-2 bg-gray-50 rounded border-l-4 border-blue-400 text-sm">
                        <p>{comment.comment}</p>
                        <p className="text-xs text-gray-500 mt-1">{comment.created_by_name} - {formatIndianDate(comment.created_at)}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setViewDialogOpen(false)} className="w-full sm:w-auto">
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Update Status Dialog */}
      <Dialog open={updateStatusDialogOpen} onOpenChange={setUpdateStatusDialogOpen}>
        <DialogContent className="max-w-sm mx-4">
          <DialogHeader>
            <DialogTitle>Update Status</DialogTitle>
            <DialogDescription className="text-sm">
              {selectedService?.service_no} - Current: <Badge className={getStatusColor(selectedService?.status)}>{selectedService?.status}</Badge>
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-4">
            {selectedService?.status === 'New' && (
              <Button onClick={() => handleStatusUpdate('On Process')} className="w-full bg-blue-600 justify-start">
                <Edit size={16} className="mr-2" /> Start Processing
              </Button>
            )}
            {(selectedService?.status === 'New' || selectedService?.status === 'On Process') && (
              <>
                <Button onClick={() => handleStatusUpdate('Closed')} className="w-full bg-green-600 justify-start">
                  <CheckCircle size={16} className="mr-2" /> Close Service
                </Button>
                <Button onClick={() => handleStatusUpdate('Canceled')} variant="destructive" className="w-full justify-start">
                  <AlertCircle size={16} className="mr-2" /> Cancel Service
                </Button>
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUpdateStatusDialogOpen(false)} className="w-full">Cancel</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Closure Dialog */}
      <Dialog open={closureDialogOpen} onOpenChange={setClosureDialogOpen}>
        <DialogContent className="max-w-sm mx-4 max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Close Service</DialogTitle>
            <DialogDescription className="text-sm">{selectedService?.service_no}</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleClosureSubmit}>
            <div className="space-y-4 py-4">
              <div>
                <Label className="text-sm">Payment Status *</Label>
                <Select value={closureData.payment_status} onValueChange={(v) => setClosureData(p => ({ ...p, payment_status: v, bill_type: '', payment_mode: '' }))}>
                  <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Paid">Paid</SelectItem>
                    <SelectItem value="Payment Pending">Payment Pending</SelectItem>
                    {selectedService?.service_type === 'AMC/MMC Service' && (
                      <SelectItem value="Contract Service">Contract Service</SelectItem>
                    )}
                  </SelectContent>
                </Select>
              </div>

              {closureData.payment_status === 'Paid' && (
                <div>
                  <Label className="text-sm">Bill Type *</Label>
                  <Select value={closureData.bill_type} onValueChange={(v) => setClosureData(p => ({ ...p, bill_type: v, payment_mode: v === 'Off Bill' ? 'Cash' : '' }))}>
                    <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="On Bill">On Bill</SelectItem>
                      <SelectItem value="Off Bill">Off Bill</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}

              {closureData.payment_status === 'Paid' && closureData.bill_type && (
                <div>
                  <Label className="text-sm">Payment Mode *</Label>
                  {closureData.bill_type === 'Off Bill' ? (
                    <Input value="Cash" disabled className="bg-gray-100" />
                  ) : (
                    <Select value={closureData.payment_mode} onValueChange={(v) => setClosureData(p => ({ ...p, payment_mode: v }))}>
                      <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Cash">Cash</SelectItem>
                        <SelectItem value="Bank Transfer">Bank Transfer</SelectItem>
                        <SelectItem value="UPI">UPI</SelectItem>
                      </SelectContent>
                    </Select>
                  )}
                </div>
              )}

              {closureData.payment_status && closureData.payment_status !== 'Contract Service' && (
                <div>
                  <Label className="text-sm">Service Charge (₹)</Label>
                  <Input type="number" value={closureData.service_charge} onChange={(e) => setClosureData(p => ({ ...p, service_charge: e.target.value }))} placeholder="Amount" />
                </div>
              )}

              <div>
                <Label className="text-sm">Notes *</Label>
                <Textarea value={closureData.payment_notes} onChange={(e) => setClosureData(p => ({ ...p, payment_notes: e.target.value }))} placeholder="Notes..." rows={3} required />
              </div>
            </div>
            <DialogFooter className="flex-col sm:flex-row gap-2">
              <Button type="button" variant="outline" onClick={() => setClosureDialogOpen(false)} className="w-full sm:w-auto">Cancel</Button>
              <Button type="submit" disabled={loading} className="w-full sm:w-auto bg-green-600">{loading ? 'Closing...' : 'Close Service'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Comment Dialog */}
      <Dialog open={commentDialogOpen} onOpenChange={setCommentDialogOpen}>
        <DialogContent className="max-w-sm mx-4">
          <DialogHeader>
            <DialogTitle>Add Note</DialogTitle>
            <DialogDescription className="text-sm">{selectedService?.service_no}</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAddComment}>
            <div className="space-y-4 py-4">
              <div>
                <Label className="text-sm">Notes *</Label>
                <Textarea value={commentData.comment} onChange={(e) => setCommentData(p => ({ ...p, comment: e.target.value }))} placeholder="Enter notes..." rows={4} required />
              </div>
              <div>
                <Label className="text-sm">Next Visit Date</Label>
                <Input type="date" value={commentData.next_visit_date} onChange={(e) => setCommentData(p => ({ ...p, next_visit_date: e.target.value }))} min={new Date().toISOString().split('T')[0]} />
              </div>
            </div>
            <DialogFooter className="flex-col sm:flex-row gap-2">
              <Button type="button" variant="outline" onClick={() => setCommentDialogOpen(false)} className="w-full sm:w-auto">Cancel</Button>
              <Button type="submit" disabled={loading} className="w-full sm:w-auto">{loading ? 'Adding...' : 'Add Note'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EngineerDashboard;
